import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { Col, Row } from "antd";
import { isEmpty, isNil, omitBy } from "lodash";
import { Button, Tabs, StackScreen } from "@life-line-lab/components-react";
import ModuleTitleBar from "Components/shared/module-title-bar/module-title-bar";
import ControlTowerAllView from "../../../components/control-tower/ct-all-view";
import ControlTowerFirstMileView from "Components/control-tower/ct-first-mile-view";
import ControlTowerMidMileView from "Components/control-tower/ct-mid-mile";
import ControlTowerLastMileView from "Components/control-tower/ct-last-mile";
import { useRecoilState } from "recoil";
import { controlTowerAtom } from "Recoil/controlTower.atom";
import { useQuery, useSession } from "Hooks/index";
import {
  sessionCreate,
  sessionDisconnect,
  listDashboardData,
  listFirstMile,
  listMidMile,
  listLastMile,
  listCustomerFirstMile,
  listCustomerMidMile,
  listCustomerLastMile,
} from "Services/controlTower.services";
import { fetchServicePointTypes } from "Services/delivery-settings.services";
import { updateTrainPlan } from "Services/delivery.services";
import { toaster } from "Helpers/toaster";
import { GlobalFilterAtom } from "Recoil/global_filter.atom";
import { UserRoleToAssign } from "Types/user.types";
import useCheckUserRole from "Hooks/check-user-role.hook";
import useCorporateType from "Hooks/corporate-type.hook";

const ControlTower = () => {
  const { org, roleTypes } = useSession();
  const { t } = useTranslation();
  const history = useHistory();
  const { isRole } = useCheckUserRole();
  const query = useQuery();
  const tabKey = query.get("tabKey") || "0";
  const [tabActiveKey, setTabActiveKey] = useState(tabKey);
  const [forceRender, setForceRender] = useState<boolean>(false);
  const [isOpen, setIsOpen] = useState(false);
  const [servicePointTypes, setServicePointTypes] = useState([]);
  const [dashboardState, setDashboardState] = useState({
    customerStatus: [],
    servicePointStatus: [],
  });
  const [firstMileState, setFirstMileState] = useState({});
  const [midMileState, setMidMileState] = useState({});
  const [lastMileState, setLastMileState] = useState({});
  const [selectedOrgID, setSelectedOrgID] = useState(org._id);
  const [controlTowerState, setControlTowerState] =
    useRecoilState<any>(controlTowerAtom);
  const [hasEventSource, setHasEventSource] = useState<any>();
  const { isCustomer } = useCorporateType();
  const isRoleCustomer = roleTypes == "CUSTOMER-SUPER_ADMIN" && true;
  const [globalFilterState] = useRecoilState(GlobalFilterAtom);

  //Open close connection useEffect
  useEffect(() => {
    callSessionCreate();
    // cleanup function to run on unmount
    handleFetchServicePointTypes();
    return () => {
      if (hasEventSource) {
        callSessionDisconnect(hasEventSource);
      }
    };
  }, []);

  useEffect(() => {
    if (
      !isEmpty(globalFilterState) &&
      globalFilterState?.selectedOrgIDs?.length
    ) {
      setSelectedOrgID(
        globalFilterState?.selectedOrgIDs?.length > 1
          ? org._id
          : globalFilterState?.selectedOrgIDs[0],
      );
    } else {
      setSelectedOrgID(org._id);
    }
  }, [globalFilterState]);

  useEffect(() => {
    if (hasEventSource) {
      handleFetchDashboardDataList({
        orgID: selectedOrgID,
        filter: { servicePointTypeName: [] },
      });
      handleFetchFirstMileList({
        orgID: selectedOrgID,
        filter: omitBy({ status: isRoleCustomer ? "ONGOING" : null }, isNil),
      });
      handleFetchMidMileList({ orgID: selectedOrgID });
      handleFetchLastMileList({ orgID: selectedOrgID });
    }
  }, [hasEventSource, selectedOrgID]);

  const handleStackScreen = () => {
    setIsOpen(!isOpen);
  };

  const handleFetchServicePointTypes = async () => {
    const responseData = await fetchServicePointTypes();
    const activeResponseType = responseData.filter(
      (item) => item.active === true,
    );
    setServicePointTypes(activeResponseType);
  };

  const handleFetchDashboardDataList = async (filters) => {
    const responseData = await listDashboardData(filters);
    setDashboardState(responseData);
  };

  const handleFetchFirstMileList = async (filters) => {
    let responseData: any;

    if (isRoleCustomer) {
      responseData = await listCustomerFirstMile(filters);
    } else {
      responseData = await listFirstMile(filters);
    }

    if (responseData && !responseData.error) setFirstMileState(responseData);
    else console.log(responseData.error);
  };

  const handleFetchMidMileList = async (filters) => {
    let responseData: any;

    if (isRoleCustomer) {
      responseData = await listCustomerMidMile(filters);
    } else {
      responseData = await listMidMile(filters);
    }

    if (responseData && !responseData.error) setMidMileState(responseData);
    else console.log(responseData.error);
  };

  const handleFetchLastMileList = async (filters) => {
    let responseData: any;

    if (isRoleCustomer) {
      responseData = await listCustomerLastMile(filters);
    } else {
      responseData = await listLastMile(filters);
    }

    if (responseData && !responseData.error) setLastMileState(responseData);
    else console.log(responseData.error);
  };

  const handleUpdateRoute = async (input) => {
    const responseData: any = await updateTrainPlan(input);

    if (!isNil(responseData) && responseData.success) {
      toaster("success", "Successfully updated train plan.");
      handleFetchMidMileList({ orgID: selectedOrgID });
    } else {
      toaster("error", "Failed to update train plan. Please try again.");
    }
  };

  //call the atom recoil to display data

  useEffect(() => {
    //set Data for different states
  }, [controlTowerState]);

  //Testing if my atom is working
  const atomTesting = () => {
    setControlTowerState((prevData) => ({ ...prevData, firstMile: "first" }));
  };

  const callSessionCreate = async () => {
    const responseData = await sessionCreate();
    setHasEventSource(responseData);
  };

  const callSessionDisconnect = async (sseEvent) => {
    const responseData = await sessionDisconnect(sseEvent);
  };

  const tabs = () => {
    if (isRole([UserRoleToAssign.PUBLIC_TRANSPORT_OPERATIONAL_USER])) {
      return [
        {
          tabKey: "2",
          label: t("tab.midMile"),
          component: (
            <ControlTowerMidMileView
              midMileData={midMileState}
              handleUpdateRoute={handleUpdateRoute}
            />
          ),
        },
      ];
    }

    if (isCustomer)
      return [
        {
          tabKey: "0",
          label: t("tab.all"),
          component: (
            <ControlTowerAllView
              servicePointDatas={dashboardState?.servicePointStatus}
              servicePointTypes={servicePointTypes}
            />
          ),
        },
        {
          tabKey: "1",
          label: t("tab.firstMile"),
          component: (
            <ControlTowerFirstMileView firstMileData={firstMileState} />
          ),
        },
        {
          tabKey: "3",
          label: t("tab.lastMile"),
          component: <ControlTowerLastMileView lastMileData={lastMileState} />,
        },
      ];

    return [
      {
        tabKey: "0",
        label: t("tab.all"),
        component: (
          <ControlTowerAllView
            servicePointDatas={dashboardState?.servicePointStatus}
            servicePointTypes={servicePointTypes}
          />
        ),
      },
      {
        tabKey: "1",
        label: t("tab.firstMile"),
        component: <ControlTowerFirstMileView firstMileData={firstMileState} />,
      },
      {
        tabKey: "2",
        label: t("tab.midMile"),
        component: (
          <ControlTowerMidMileView
            midMileData={midMileState}
            handleUpdateRoute={handleUpdateRoute}
          />
        ),
      },
      {
        tabKey: "3",
        label: t("tab.lastMile"),
        component: <ControlTowerLastMileView lastMileData={lastMileState} />,
      },
    ];
  };

  const handleTabClick = (key) => {
    let path = "";

    if (key === "0") {
      path = `/delivery-management/control-tower`;
    } else {
      path = `/delivery-management/control-tower?tabKey=${key}`;
    }

    setTabActiveKey(key);
    setForceRender(true);
    history.push(path);
  };

  const ControlTowerMain = () => {
    return (
      <>
        {isOpen ? (
          <></>
        ) : (
          <ModuleTitleBar titleText={t("pageTitle.controlTower")} />
        )}
        <div className="control-tower">
          <div>
            <Row style={{ justifyContent: "flex-end" }}></Row>
            <Row style={{ padding: "15px 0 0 15px" }}>
              <Col xs={24} sm={24} md={24} xl={24} className="tabs-container">
                <Tabs
                  tabBarExtraContent={
                    <>
                      <Row style={{ marginRight: "40px" }}>
                        <Col style={{ marginRight: "20px" }}>
                          {/*<DataTableFilter*/}
                          {/*  // searchLabel={t("label.search")}*/}
                          {/*  // searchPlaceholder={t("txt.searchPlaceholder")}*/}
                          {/*  // onSearch={onSearch}*/}
                          {/*/>*/}
                        </Col>
                        {isOpen ? (
                          <> </>
                        ) : (
                          <Col style={{ marginLeft: "20px" }}>
                            <Button
                              className="fullscreen-button"
                              type="primary"
                              label={t("action.viewFullscreen")}
                              size="large"
                              onClick={handleStackScreen}
                              // onClick={atomTesting}
                            ></Button>
                          </Col>
                        )}
                      </Row>
                    </>
                  }
                  destroyInactiveTabPane={true}
                  onTabClick={handleTabClick}
                  defaultActiveKey="0"
                  activeKey={tabActiveKey}
                  tabBarGutter={25}
                  tabs={tabs()}
                />
              </Col>
            </Row>
          </div>
        </div>
      </>
    );
  };

  return (
    <>
      <Helmet>
        <title>{t("pageTitle.controlTower")}</title>
      </Helmet>
      {isOpen ? (
        <StackScreen
          title={t("pageTitle.controlTower")}
          isOpen={isOpen}
          renderHeader={<div></div>}
          onClose={handleStackScreen}
        >
          <ControlTowerMain />
        </StackScreen>
      ) : (
        <div>
          <ControlTowerMain />
        </div>
      )}
    </>
  );
};

export default ControlTower;
